import { useAsync, UseAsyncStatus } from '@shared/async';
import { useErrorHandler } from '@shared/errors';
import { Text } from 'grommet';
import React, { useEffect, useId, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Busy } from '@/components/busy';
import { Dialog, DialogActions, DialogBody, DialogHeader } from '@/components/dialog';
import { Button } from '@/components-new/button';
import { ChangeLogReason } from '@/features/changelog/types';
import { EditCoverageRequest, useCoverageService } from '@/features/coverage/api/use-coverage-service';
import { ClinicalsProductEditForm } from '@/features/coverage/components/clinicals-product-edit-form';
import { notifySuccess } from '@/lib/notification/notifications';
import { formatNumber } from '@/utils/formatting';

import { FilterInputs } from './index/coverage-filters';

type BulkPdlEditDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  filters: FilterInputs;
  idsToUpdate: number[];
};

export const BulkPdlEditDialog = ({ open, onClose, onSuccess, filters, idsToUpdate }: BulkPdlEditDialogProps) => {
  const [visibleFields, setVisibleFields] = useState<(keyof EditCoverageRequest)[]>([]);
  const [dataToConfirm, setDataToConfirm] = useState<EditCoverageRequest | null>(null);


  const { bulkEditCoverage } = useCoverageService();
  const request = useAsync(bulkEditCoverage);
  const { handleError } = useErrorHandler();

  useEffect(() => {
    const { status, error } = request;
    if (status === UseAsyncStatus.Pending || status === UseAsyncStatus.Idle) return;

    if (status === UseAsyncStatus.Error) {
      handleError(error, { title: 'Failed to bulk update coverage records' });

      return;
    }

    notifySuccess({ title: `Updated ${idsToUpdate.length} Coverage Records` });
    clearFields();
    onSuccess();
    // TODO: review this effect, it's missing a lot of dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.status]);

  const saving = request.status === UseAsyncStatus.Pending;
  const submitDisabled = saving || visibleFields.length === 0;

  const formId = useId();
  const form = useForm<EditCoverageRequest>({
    defaultValues: {
      // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'PdlStatus |... Remove this comment to see the full error message
      pdlStatus: null,
      pdlStatusDate: '',
      pdlStatusEffectiveDate: '',
      paTypes: [],
      stepTherapyCount: '',
      paCriteriaDescription: '',
      paCriteriaSourceUrl: '',
      changeReason: ChangeLogReason.PDL_UPDATE,
      isStatePaCriteriaSameAsPdlLink: false
    }
  });

  const { handleSubmit, reset } = form;

  const clearFields = () => {
    setVisibleFields([]);
    setDataToConfirm(null);
    reset();
  };

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const onFieldShow = (field: keyof EditCoverageRequest) => {
    setVisibleFields(x => [...x, field]);
  };

  const onFieldHide = (field: keyof EditCoverageRequest) => {
    setVisibleFields(x => x.filter(it => it !== field));
  };

  const canUseBulkPdlEdit = filters.states && filters.states.length > 0;

  return (
    <FormProvider {...form}>
      <Dialog open={open} width="large">
        <DialogHeader
          title="Bulk PDL Edit"
          subtitle={canUseBulkPdlEdit ? `Updating ${formatNumber(idsToUpdate.length, 0, 0)} Coverage Records` : null}
          onClose={handleClose}
        />
        <DialogBody>
          {canUseBulkPdlEdit ? (
            <ClinicalsProductEditForm
              formId={formId}
              mode="bulk-edit"
              onSubmit={handleSubmit((data) => setDataToConfirm(data))}
              visibleFields={visibleFields}
              onFieldShow={onFieldShow}
              onFieldHide={onFieldHide}
              filteredDrugIds={filters.products?.map(it => it.id) ?? []}
            />
          ) : (
            <Text>You must select at least one State or Pool to use this functionality.</Text>
          )}
        </DialogBody>
        {canUseBulkPdlEdit ? (
          <DialogActions>
            {dataToConfirm !== null ? (
              <Text weight="bold" color="error">Are you sure you want to update {formatNumber(idsToUpdate.length, 0, 0)} Coverage records?</Text>
            ) : null}
            <Button
              plain
              onClick={() => {
                if (dataToConfirm !== null) {
                  setDataToConfirm(null);
                } else {
                  handleClose();
                }
              }}
            >
              Cancel
            </Button>
            {dataToConfirm !== null ? (
              <Button
                disabled={saving}
                onClick={() => {
                  void request.execute(idsToUpdate, dataToConfirm, visibleFields);
                }}
              >
                <Busy busy={saving} content="Confirm"/>
              </Button>
            ) : (
              <Button
                type="submit"
                form={formId}
                disabled={submitDisabled}
              >
                Save
              </Button>
            )}
          </DialogActions>
        ) : (
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
          </DialogActions>
        )}
      </Dialog>
    </FormProvider>
  );
};
